import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { TripResponse } from '@/shared/api/types'
import { WaypointResponse, RouteResponse } from '@/shared/api/routes'
import { useAuth } from '@/modules/auth'
import { useTripWaypoints } from '@/modules/trips/hooks/useTripWaypoints'
import { useWaypointActions } from '@/modules/routes/hooks/useWaypointActions'
import { useTrips } from '@/modules/trips'
import { useClientSideRoutes } from '@/modules/routes/hooks/useClientSideRoutes'
import { TripsApi } from '@/shared/api/trips'
import { organizeWaypointsByDay } from '../utils/itineraryHelpers'

// Color palette for different days
const DAY_COLORS = [
  '#5C8DC5', // Primary blue - Day 1
  '#14b8a6', // Teal - Day 2 (high contrast with roads and map)
  '#AD9E90', // Accent 1 - Day 3
  '#736F60', // Accent 2 - Day 4
  '#8B9DC3', // Light blue - Day 5
  '#A89B8D', // Light brown - Day 6
  '#9B9B9B', // Gray - Day 7
]

export type ViewMode = 'trips-list' | 'itinerary-view'

export interface MapClickCoordinates {
  latitude: number
  longitude: number
}

// Mutation tracker for coordinating waypoint operations
type MutationType = 'delete' | 'reorder' | 'update_day' | 'add'
interface MutationTracker {
  type: MutationType
  waypointIds: string[]
  timestamp: number
  affectedDays?: number[]
}

// Helper to get affected days from waypoints
function getAffectedDays(waypoints: WaypointResponse[], waypointIds: string[]): number[] {
  const days = new Set<number>()
  waypoints.forEach(wp => {
    if (waypointIds.includes(wp.id) && wp.trip_day) {
      days.add(wp.trip_day)
    }
  })
  return Array.from(days)
}

// Helper to clear routes optimistically for specific days
function clearRoutesForDays(routes: RouteResponse[], days: number[]): RouteResponse[] {
  if (days.length === 0) return routes
  return routes.filter(route => !days.includes(route.day ?? 0))
}

interface UseItineraryBuilderOptions {
  libraryWaypoints?: WaypointResponse[]
}

export function useItineraryBuilder(options: UseItineraryBuilderOptions = {}) {
  const { libraryWaypoints = [] } = options
  const params = useParams<{ tripid?: string; waypointid?: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { getAccessToken: getToken } = useAuth()
  const { trips } = useTrips()

  // Get Mapbox access token from environment
  const mapboxToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN || null

  const [selectedTrip, setSelectedTrip] = useState<TripResponse | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('trips-list')
  const [mapClickCoordinates, setMapClickCoordinates] = useState<MapClickCoordinates | null>(null)
  const [route, setRoute] = useState<RouteResponse | null>(null)
  const [selectedWaypoint, setSelectedWaypoint] = useState<WaypointResponse | null>(null)
  const [centerOnWaypoint, setCenterOnWaypoint] = useState<WaypointResponse | null>(null)
  
  // Track recently clicked waypoints to prevent race conditions
  const recentlyClickedWaypointRef = useRef<{ id: string; timestamp: number } | null>(null)

  // Get trip ID and waypoint ID from URL params or query params
  const tripId = params.tripid || searchParams.get('tripId') || null
  const waypointId = params.waypointid || null

  // Fetch trip when URL param changes
  useEffect(() => {
    if (tripId) {
      const trip = trips?.find(t => t.id === tripId)
      if (trip) {
        setSelectedTrip(trip)
        setViewMode('itinerary-view')
      } else if (trips && trips.length > 0) {
        // Trip not found, navigate back to list
        navigate('/explore/trips')
      }
    } else {
      setSelectedTrip(null)
      // Only set to trips-list if there's no waypoint selected either
      if (!waypointId) {
        setViewMode('trips-list')
      }
    }
  }, [tripId, waypointId, trips, navigate])

  // Use trip waypoints hook (only when tripId exists)
  // Pass undefined instead of empty string to prevent unnecessary fetches
  const {
    waypoints: serverWaypoints,
    loading: waypointsLoading,
    fetchWaypoints,
    reorderWaypoints,
    deleteWaypoint
  } = useTripWaypoints({
    tripId: tripId || undefined
  })

  // Optimistic waypoint state - merged with server state
  const [optimisticWaypoints, setOptimisticWaypoints] = useState<Map<string, WaypointResponse>>(new Map())
  const [deletedWaypointIds, setDeletedWaypointIds] = useState<Set<string>>(new Set())

  // Merge server waypoints with optimistic updates
  const waypoints = useMemo(() => {
    let merged = [...serverWaypoints]
    
    // Apply optimistic updates
    optimisticWaypoints.forEach((optimisticWp, id) => {
      const index = merged.findIndex(wp => wp.id === id)
      if (index >= 0) {
        merged[index] = optimisticWp
      } else {
        merged.push(optimisticWp)
      }
    })
    
    // Filter out deleted waypoints
    merged = merged.filter(wp => !deletedWaypointIds.has(wp.id))
    
    // Sort by order_index
    return merged.sort((a, b) => {
      const orderA = a.order_index ?? 999999
      const orderB = b.order_index ?? 999999
      return orderA - orderB
    })
  }, [serverWaypoints, optimisticWaypoints, deletedWaypointIds])

  // Mutation tracker - more sophisticated than simple boolean
  const activeMutationsRef = useRef<MutationTracker[]>([])

  // Generate routes client-side using Mapbox Directions API
  const { routes: clientRoutes, isLoading: routesLoading, error: routesError } = useClientSideRoutes({
    waypoints, // Use merged waypoints (with optimistic updates)
    tripId,
    profile: 'driving',
    enabled: !!tripId && waypoints.length > 0,
    accessToken: mapboxToken
  })

  // Update routeSegments when clientRoutes change
  const [routeSegments, setRouteSegments] = useState<RouteResponse[]>([])
  
  useEffect(() => {
    setRouteSegments(clientRoutes)
    if (clientRoutes.length > 0) {
      setRoute(clientRoutes[0])
    } else {
      setRoute(null)
    }
  }, [clientRoutes])

  // Use waypoint actions hook
  const waypointActions = useWaypointActions({
    tripId,
    routeId: null, // Trip-level waypoints
    fetchWaypoints: async () => {
      if (tripId) {
        await fetchWaypoints()
      }
      return waypoints
    },
    onWaypointCreated: (newWaypoint: WaypointResponse) => {
      // Immediately add the waypoint to optimistic state instead of refetching
      // This ensures the waypoint appears instantly without waiting for a fetch
      if (tripId && newWaypoint) {
        // Calculate order_index from server waypoints (before optimistic updates)
        // This ensures we get the correct max order_index
        const currentMaxOrder = serverWaypoints.length > 0 
          ? Math.max(...serverWaypoints.map(wp => wp.order_index ?? 0))
          : -1
        const calculatedOrderIndex = newWaypoint.order_index ?? (currentMaxOrder + 1)
        
        // Enhance waypoint with junction fields that may not be in API response
        // The API returns waypoint from waypoints table, but junction fields (trip_id, trip_day, order_index)
        // are stored separately in trip_waypoints table
        // We need these fields for the waypoint to pass the filter in itineraryDays
        const enhancedWaypoint: WaypointResponse = {
          ...newWaypoint,
          // CRITICAL: Ensure trip_id is set from context (API response doesn't include it from junction table)
          trip_id: tripId,
          // Preserve trip_day if it exists, otherwise default to 1 (matching handleSaveSearchResultAsWaypoint)
          trip_day: newWaypoint.trip_day ?? 1,
          // Use calculated order_index
          order_index: calculatedOrderIndex
        }
        
        setOptimisticWaypoints(prev => {
          const updated = new Map(prev)
          // Only add if it's not already there (avoid duplicates)
          if (!updated.has(newWaypoint.id)) {
            updated.set(enhancedWaypoint.id, enhancedWaypoint)
          }
          return updated
        })
        
        // Also refresh waypoints in the background with cache-busting to ensure consistency
        // But don't wait for it - the optimistic update is immediate
        // Use a small delay to ensure the junction table entry is committed before fetching
        setTimeout(() => {
          fetchWaypoints({ cache: 'no-store' }).catch(() => {
            // Non-critical - optimistic update is already shown
          })
        }, 100)
        // Routes will regenerate automatically via useClientSideRoutes hook
      }
    }
  })

  // Routes are now generated client-side via useClientSideRoutes hook
  // No need to fetch routes when trip changes - hook handles it automatically

  const handleTripSelect = useCallback((trip: TripResponse) => {
    navigate(`/explore/trip/${trip.id}`)
  }, [navigate])

  const handleBackToTrips = useCallback(() => {
    // Clear waypoint ID when navigating back to trips list
    // Don't use replace: true to preserve proper browser history
    navigate('/explore/trips')
    setSelectedWaypoint(null)
    setCenterOnWaypoint(null)
  }, [navigate])

  const handleMapClick = useCallback((coords: MapClickCoordinates) => {
    setMapClickCoordinates(coords)
  }, [])

  const clearMapClick = useCallback(() => {
    setMapClickCoordinates(null)
  }, [])

  const handleAddStop = useCallback(async (options?: { poi_type?: 'gas' | 'water' | 'viewpoint' | 'campsite' | 'meetup_spot' | 'trailhead' | 'custom' | null; poi_custom_label?: string | null }) => {
    if (!mapClickCoordinates || !tripId) return

    // Track mutation
    const mutation: MutationTracker = {
      type: 'add',
      waypointIds: [],
      timestamp: Date.now()
    }
    activeMutationsRef.current.push(mutation)

    try {
      // 1. Add waypoint (useTripWaypoints hook already has optimistic updates)
      // Waypoint appears immediately in UI via optimistic update
      // Default to null (Route Stop) if no type specified - user can edit type after creation
      await waypointActions.handleSaveMapPointAsWaypoint(mapClickCoordinates, options)
      
      // 2. Routes will regenerate automatically via useClientSideRoutes hook

      setMapClickCoordinates(null)

    } catch (error) {

    } finally {
      // Remove mutation tracker
      activeMutationsRef.current = activeMutationsRef.current.filter(m => m !== mutation)
    }
  }, [mapClickCoordinates, tripId, waypointActions])

  const handleReorderWaypoints = useCallback(async (waypointIds: string[]) => {
    if (!tripId) return
    
    // Store current route geometry before reorder for comparison
    const routesBeforeReorder = [...routeSegments]
    
    // Get affected days from current waypoint order
    const affectedDays = getAffectedDays(waypoints, waypointIds)
    
    // Track mutation
    const mutation: MutationTracker = {
      type: 'reorder',
      waypointIds,
      timestamp: Date.now(),
      affectedDays
    }
    activeMutationsRef.current.push(mutation)
    
    // 1. OPTIMISTIC UPDATE: Immediately update order_index in local state
    setOptimisticWaypoints(prev => {
      const updated = new Map(prev)
      waypointIds.forEach((id, index) => {
        const wp = waypoints.find(w => w.id === id)
        if (wp) {
          updated.set(id, { ...wp, order_index: index })
        }
      })
      return updated
    })

    try {
      // 2. Reorder waypoints on backend (route regeneration happens in background)
      await reorderWaypoints(waypointIds)
      
      // 3. Refetch waypoints to get the updated order_index from server
      // This ensures the UI stays in sync with server state
      await fetchWaypoints({ cache: 'no-store' })
      
      // Clear optimistic update now that we have fresh server data
      setOptimisticWaypoints(prev => {
        const updated = new Map(prev)
        waypointIds.forEach(id => updated.delete(id))
        return updated
      })
      
      // 4. Routes will regenerate automatically via useClientSideRoutes hook
      
    } catch (error) {

      // Rollback optimistic update on error
      setOptimisticWaypoints(prev => {
        const updated = new Map(prev)
        waypointIds.forEach(id => updated.delete(id))
        return updated
      })
      
      // Refetch to restore correct state (with cache-busting)
      await fetchWaypoints({ cache: 'no-store' })
    } finally {
      // Remove mutation tracker
      activeMutationsRef.current = activeMutationsRef.current.filter(m => m !== mutation)
    }
  }, [tripId, waypoints, reorderWaypoints, fetchWaypoints])

  const handleDeleteWaypoint = useCallback(async (waypointId: string) => {
    if (!tripId) return
    
    // Find waypoint to get affected day
    const waypoint = waypoints.find(wp => wp.id === waypointId)
    const affectedDay = waypoint?.trip_day
    
    // Track mutation
    const mutation: MutationTracker = {
      type: 'delete',
      waypointIds: [waypointId],
      timestamp: Date.now(),
      affectedDays: affectedDay ? [affectedDay] : undefined
    }
    activeMutationsRef.current.push(mutation)
    
    // 1. OPTIMISTIC UPDATE: Immediately remove waypoint from UI
    setDeletedWaypointIds(prev => new Set([...prev, waypointId]))
    
    // 2. OPTIMISTIC UPDATE: Clear selected waypoint if it's the deleted one
    // This prevents the blue dot marker from staying on the map
    if (selectedWaypoint?.id === waypointId) {
      setSelectedWaypoint(null)
      setCenterOnWaypoint(null)
    }
    
    // 3. OPTIMISTIC UPDATE: Immediately clear routes for affected day
    if (affectedDay) {
      setRouteSegments(prev => clearRoutesForDays(prev, [affectedDay]))
    }

    try {
      // 3. Delete waypoint on backend (triggers auto-regeneration)
      await deleteWaypoint(waypointId)
      
      // 4. Small delay to ensure server has processed the delete before fetching
      await new Promise(resolve => setTimeout(resolve, 200))
      
      // 5. Refresh waypoints from server with cache-busting (will clear optimistic delete)
      // Use 'no-store' to bypass HTTP cache and get fresh data after deletion
      await fetchWaypoints({ cache: 'no-store' })
      
      // 6. Routes will regenerate automatically via useClientSideRoutes hook
      
    } catch (error) {

      // Rollback optimistic update on error
      setDeletedWaypointIds(prev => {
        const updated = new Set(prev)
        updated.delete(waypointId)
        return updated
      })
      
      // Refetch to restore correct state (with cache-busting)
      await fetchWaypoints({ cache: 'no-store' })
    } finally {
      // Remove mutation tracker
      activeMutationsRef.current = activeMutationsRef.current.filter(m => m !== mutation)
      
      // Don't clear optimistic delete state immediately - keep it until we've confirmed
      // the server has the updated data. The fetchWaypoints call above will update the
      // server waypoints, which will naturally clear the optimistic delete when merged.
      // Only clear if there was an error (handled in catch block)
    }
  }, [tripId, waypoints, selectedWaypoint, deleteWaypoint, fetchWaypoints])

  const handleUpdateWaypointDay = useCallback(async (waypointId: string, day: number) => {
    if (!tripId) return

    // Find waypoint to get old day
    const waypoint = waypoints.find(wp => wp.id === waypointId)
    const oldDay = waypoint?.trip_day
    const affectedDays: number[] = []
    if (oldDay) affectedDays.push(oldDay)
    if (day !== oldDay) affectedDays.push(day)
    
    // Calculate order_index for the new day (append to end of day)
    const waypointsOnTargetDay = waypoints.filter(wp => wp.trip_day === day && wp.id !== waypointId)
    const maxOrderIndex = waypointsOnTargetDay.reduce((max, wp) => {
      const orderIndex = wp.order_index ?? 0
      return Math.max(max, orderIndex)
    }, -1)
    const newOrderIndex = maxOrderIndex + 1
    
    // Track mutation
    const mutation: MutationTracker = {
      type: 'update_day',
      waypointIds: [waypointId],
      timestamp: Date.now(),
      affectedDays
    }
    activeMutationsRef.current.push(mutation)
    
    // 1. OPTIMISTIC UPDATE: Immediately update waypoint day in UI
    setOptimisticWaypoints(prev => {
      const updated = new Map(prev)
      const wp = waypoints.find(w => w.id === waypointId)
      if (wp) {
        updated.set(waypointId, { ...wp, trip_day: day, order_index: newOrderIndex })
      }
      return updated
    })
    
    // 2. OPTIMISTIC UPDATE: Immediately clear routes for affected days
    if (affectedDays.length > 0) {
      setRouteSegments(prev => clearRoutesForDays(prev, affectedDays))
    }

    try {
      // 3. Update waypoint on backend using trip-specific endpoint (triggers auto-regeneration)
      // Use the dedicated trip-waypoint junction table endpoint
      const tripsApi = new TripsApi(getToken)
      const updateResult = await tripsApi.updateTripWaypoint(tripId, waypointId, {
        trip_day: day,
        order_index: newOrderIndex
      })
      
      // 4. Update optimistic state with server response (ensures consistency)
      // The server response is the source of truth, so use it to update optimistic state
      if (updateResult) {
        setOptimisticWaypoints(prev => {
          const updated = new Map(prev)
          // Find the waypoint in current waypoints to merge with server response
          const currentWp = waypoints.find(w => w.id === waypointId)
          const mergedWaypoint = currentWp ? {
            ...currentWp,
            ...updateResult,
            trip_day: updateResult.trip_day,
            order_index: updateResult.order_index
          } : updateResult
          
          updated.set(waypointId, mergedWaypoint)
          
          // Also update selectedWaypoint immediately if it's the one being updated
          if (selectedWaypoint?.id === waypointId) {
            setSelectedWaypoint(mergedWaypoint)
          }
          
          return updated
        })
      }
      
      // 5. Routes will regenerate automatically via useClientSideRoutes hook

      // 6. Refresh waypoints from server after a short delay to ensure server has processed
      // This ensures the UI stays in sync, but we've already updated optimistic state above
      setTimeout(async () => {
        try {
          await fetchWaypoints({ cache: 'no-store' })
          // Clear optimistic update after server confirms (waypoints memo will use server data)
          setOptimisticWaypoints(prev => {
            const updated = new Map(prev)
            updated.delete(waypointId)
            return updated
          })
        } catch (error) {
        }
      }, 500)

    } catch (error) {

      // Rollback optimistic update on error
      setOptimisticWaypoints(prev => {
        const updated = new Map(prev)
        updated.delete(waypointId)
        return updated
      })
      
      // Refetch to restore correct state (with cache-busting)
      await fetchWaypoints({ cache: 'no-store' })
      throw error
    } finally {
      // Remove mutation tracker
      activeMutationsRef.current = activeMutationsRef.current.filter(m => m !== mutation)
      
      // Clear optimistic state after delay
      setTimeout(() => {
        setOptimisticWaypoints(prev => {
          const updated = new Map(prev)
          updated.delete(waypointId)
          return updated
        })
      }, 1000)
    }
  }, [tripId, waypoints, getToken, fetchWaypoints])

  const handleUpdateWaypoint = useCallback(async (waypointId: string, updates: { poi_type?: string | null; poi_custom_label?: string | null; name?: string; notes?: string }) => {
    if (!tripId) {

      return
    }

    // Find the current waypoint for optimistic update
    const currentWaypoint = waypoints.find(wp => wp.id === waypointId)
    if (!currentWaypoint) {
      return
    }

    // Normalize updates: preserve null for nullable fields like poi_type
    const normalizedUpdates: any = {
      ...updates,
      // poi_type: Preserve null (for "None") and undefined (for no change)
      // - null: set to null (no label)
      // - undefined: don't update this field
      // - string: set to the specified type
      poi_type: updates.poi_type !== undefined ? updates.poi_type : undefined,
      name: updates.name !== undefined ? updates.name : undefined,
      notes: updates.notes !== undefined ? updates.notes : undefined
      // poi_custom_label: Keep as-is (can be string, null, or undefined)
      // - string: set the custom label
      // - null: clear the custom label
      // - undefined: don't update this field
      // We don't need to normalize it, just pass it through
    }

    // Optimistic update - update UI immediately
    const optimisticWaypoint: WaypointResponse = {
      ...currentWaypoint,
      ...normalizedUpdates,
      // Handle null explicitly - null means "no label", undefined means "don't change"
      poi_type: normalizedUpdates.poi_type !== undefined ? normalizedUpdates.poi_type : currentWaypoint.poi_type,
      poi_custom_label: normalizedUpdates.poi_custom_label !== undefined ? normalizedUpdates.poi_custom_label : currentWaypoint.poi_custom_label,
      name: normalizedUpdates.name !== undefined ? normalizedUpdates.name : currentWaypoint.name,
      notes: normalizedUpdates.notes !== undefined ? normalizedUpdates.notes : currentWaypoint.notes
    }

    setOptimisticWaypoints(prev => {
      const updated = new Map(prev)
      updated.set(waypointId, optimisticWaypoint)
      return updated
    })

    // Update selectedWaypoint if it's the one being updated
    if (selectedWaypoint?.id === waypointId) {
      setSelectedWaypoint(optimisticWaypoint)
    }

    try {
      // Update via API
      const updatedWaypoint = await waypointActions.updateWaypoint(waypointId, normalizedUpdates)
      
      // Update optimistic state with server response (more accurate)
      // Preserve junction fields from current waypoint since server response might not include them
      // (These fields are stored in trip_waypoints junction table, not waypoints table)
      if (updatedWaypoint) {
        const preservedWaypoint: WaypointResponse = {
          ...updatedWaypoint,
          // Preserve trip_id - critical for marker filtering
          trip_id: currentWaypoint.trip_id ?? updatedWaypoint.trip_id ?? tripId,
          order_index: currentWaypoint.order_index ?? updatedWaypoint.order_index,
          trip_day: currentWaypoint.trip_day ?? updatedWaypoint.trip_day,
          start_time: currentWaypoint.start_time ?? updatedWaypoint.start_time,
          end_time: currentWaypoint.end_time ?? updatedWaypoint.end_time
        }
        
        setOptimisticWaypoints(prev => {
          const updated = new Map(prev)
          updated.set(waypointId, preservedWaypoint)
          return updated
        })
        
        // Update selectedWaypoint with server response if it's the one being updated
        if (selectedWaypoint?.id === waypointId) {
          setSelectedWaypoint(preservedWaypoint)
        }
      }
      
      // POI label updates (poi_type, poi_custom_label) don't affect ordering,
      // so we don't need to refetch the waypoints list
    } catch (error) {
      // Revert optimistic update on error
      setOptimisticWaypoints(prev => {
        const updated = new Map(prev)
        updated.delete(waypointId)
        return updated
      })
      // Revert selectedWaypoint if it's the one being updated
      if (selectedWaypoint?.id === waypointId) {
        setSelectedWaypoint(currentWaypoint)
      }
      throw error
    }
  }, [tripId, waypointActions, waypoints, fetchWaypoints, selectedWaypoint])

  const handleAddWaypointToDay = useCallback((day: number) => {
    // For now, this just shows a message that user should click on the map
    // Future: Could open a dialog or trigger map click mode with day pre-selected
    if (!tripId) {

      return
    }
    // Store the target day for future map clicks
    // This is a simple implementation - can be enhanced later

  }, [tripId])

  // Handle waypoint click - navigates to waypoint URL and sets selected waypoint
  const handleWaypointClick = useCallback((waypoint: WaypointResponse) => {
    // Track that we just clicked this waypoint to prevent race conditions
    recentlyClickedWaypointRef.current = {
      id: waypoint.id,
      timestamp: Date.now()
    }
    
    // If we're viewing a trip, just select the waypoint and center the map
    // Don't navigate away from the trip URL - keep trip context
    if (tripId) {
      setSelectedWaypoint(waypoint)
      setCenterOnWaypoint(waypoint)
      
      // Clear the ref after 2 seconds
      setTimeout(() => {
        if (recentlyClickedWaypointRef.current?.id === waypoint.id) {
          recentlyClickedWaypointRef.current = null
        }
      }, 2000)
      return
    }
    
    // If not viewing a trip, navigate to waypoint URL (library view, etc.)
    navigate(`/explore/waypoint/${waypoint.id}`)
    setSelectedWaypoint(waypoint)
    setCenterOnWaypoint(waypoint)
    
    // Clear the ref after 2 seconds (enough time for the effect to run and find the waypoint)
    setTimeout(() => {
      if (recentlyClickedWaypointRef.current?.id === waypoint.id) {
        recentlyClickedWaypointRef.current = null
      }
    }, 2000)
  }, [navigate, tripId])

  // Keep selectedWaypoint in sync with waypoints array (for optimistic updates)
  // This ensures the details panel shows the latest data when waypoints are updated
  useEffect(() => {
    if (selectedWaypoint?.id && tripId) {
      // Find the updated waypoint in the merged waypoints array
      const updatedWaypoint = waypoints.find(wp => wp.id === selectedWaypoint.id)
      if (updatedWaypoint) {
        // Check if any relevant fields have changed (not just reference equality)
        const hasChanges = 
          updatedWaypoint.poi_type !== selectedWaypoint.poi_type ||
          updatedWaypoint.poi_custom_label !== selectedWaypoint.poi_custom_label ||
          updatedWaypoint.name !== selectedWaypoint.name ||
          updatedWaypoint.notes !== selectedWaypoint.notes ||
          updatedWaypoint.trip_day !== selectedWaypoint.trip_day
        
        // Only update if there are actual field changes to avoid infinite loops
        if (hasChanges) {
          setSelectedWaypoint(updatedWaypoint)
          setCenterOnWaypoint(updatedWaypoint)
        }
      }
    }
    // Only depend on waypoints array, not selectedWaypoint to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypoints, tripId])

  // Sync selected waypoint with URL param
  useEffect(() => {
    if (waypointId) {
      // Check if we just clicked this waypoint (within last 2 seconds)
      const justClicked = recentlyClickedWaypointRef.current?.id === waypointId
      
      // Try to find waypoint in trip waypoints first (if trip is loaded)
      let foundWaypoint = tripId ? waypoints.find(wp => wp.id === waypointId) : null
      
      // If not found in trip waypoints, check library waypoints
      if (!foundWaypoint && libraryWaypoints.length > 0) {
        foundWaypoint = libraryWaypoints.find(wp => wp.id === waypointId)
      }
      
      // If still not found, wait if we're expecting library waypoints to load
      if (!foundWaypoint) {
        // If we just clicked this waypoint, keep the current selection
        // This prevents the race condition where the effect runs before libraryWaypoints updates
        if (justClicked || selectedWaypoint?.id === waypointId) {
          // Keep the current selection - don't clear it
          return
        }
        
        // If we're on a waypoint URL but don't have library waypoints yet, wait
        if (!tripId && libraryWaypoints.length === 0) {
          // Library waypoints might still be loading, so don't clear selection yet
          return
        }
        
        // Only clear if we're sure the waypoint doesn't exist and we didn't just click it
        setSelectedWaypoint(null)
        setCenterOnWaypoint(null)
        return
      }
      
      // Found the waypoint - only update if it's different from current selection
      // But if we just clicked it and already have it selected, don't update (prevents flicker)
      if (foundWaypoint.id !== selectedWaypoint?.id) {
        setSelectedWaypoint(foundWaypoint)
        setCenterOnWaypoint(foundWaypoint)
      }
      
      // If waypoint belongs to a trip, set that trip (but don't change URL)
      // This allows viewing waypoint with trip context on waypoint URL
      if (foundWaypoint.trip_id && foundWaypoint.trip_id !== tripId) {
        const trip = trips?.find(t => t.id === foundWaypoint.trip_id)
        if (trip) {
          setSelectedTrip(trip)
          setViewMode('itinerary-view')
        }
      }
    } else {
      // Clear selection if waypoint ID is removed from URL
      // But preserve selection if we're just navigating within a trip
      if (!tripId) {
        setSelectedWaypoint(null)
        setCenterOnWaypoint(null)
      }
    }
  }, [waypointId, tripId, waypoints, libraryWaypoints, trips, selectedWaypoint])

  return {
    selectedTrip,
    viewMode,
    mapClickCoordinates,
    waypoints: tripId ? waypoints : [],
    waypointsLoading,
    route,
    routeSegments,
    routesLoading,
    selectedWaypoint,
    setSelectedWaypoint,
    centerOnWaypoint,
    setCenterOnWaypoint,
    handleWaypointClick,
    handleTripSelect,
    handleBackToTrips,
    handleMapClick,
    clearMapClick,
    handleAddStop,
    handleReorderWaypoints,
    handleDeleteWaypoint,
    handleUpdateWaypointDay,
    handleUpdateWaypoint,
    handleAddWaypointToDay,
    fetchWaypoints,
    waypointActions // Expose waypointActions so ItineraryBuilderPage can use it
  }
}
